/** @file stripOrigins: the strip origin each uploaded cell implies; cells of one entry must agree. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { findSlot } from '../slot/findSlot';
import { pointerToken } from '../validate/pointerToken';

/**
 * Per `<drawable>/<entry>` the origin `[x − tile·w, y]` (`[x, y − tile·h]` with `extendX`, 18.5) of its cells;
 * cells of one entry that imply different origins throw `VALIDATION` naming both keys.
 */
export function stripOrigins(puppet: Puppet, cells: Readonly<Record<string, [number, number]>>): Map<string, [number, number]> {
  const out = new Map<string, [number, number]>();
  const firstKey = new Map<string, string>();
  for (const [key, [x, y]] of Object.entries(cells)) {
    const path = `/input/cells/${pointerToken(key)}`;
    const { slot, entry } = findSlot(puppet, key, path);
    const [w, h] = entry.size;
    const origin: [number, number] = entry.extendX === true ? [x, y - slot.tile * h] : [x - slot.tile * w, y];
    const id = `${slot.drawable}/${slot.entry}`;
    const seen = out.get(id);
    if (seen !== undefined && (seen[0] !== origin[0] || seen[1] !== origin[1])) {
      throwError(ERROR_CODES.VALIDATION, { cells: [firstKey.get(id), key] }, { path, hint: `cells of ${id} lie on one strip: ${String(firstKey.get(id))} and ${key} disagree` });
    }
    out.set(id, origin);
    if (!firstKey.has(id)) firstKey.set(id, key);
  }
  return out;
}
